/**
 * Episode rollup wire contract (PRD G2–G3; increment-8 plan §1 PR3) — the
 * derived-on-read compliance picture one episode serves to its dashboards:
 * the compliance rollup, the per-day heatmap cells, and the observation-daily
 * chart series.
 *
 * ONE route serves all of it (`GET /api/episodes/:episodeId/rollup`), because
 * it is ONE picture with one derivation instant: cells, counts and series must
 * agree about what "today" is, which two separately-fetched routes could not
 * guarantee. Nothing here is ever persisted (E2 posture) — the response is
 * recomputed from confirmed records on every read.
 *
 * The arithmetic (validity rule, rep-compliance counting, bucket boundaries)
 * is `@advaitnetra/protocol` rollup.ts; the adherence derivation is the flag
 * engine's own (`adherenceTrips` + its counts query), so a displayed adherence
 * and an ADHER_70 raise can never disagree. This module is only the shape.
 */

import { z } from 'zod';

import { flagCodeWireSchema } from './flags';

/** The four fill buckets; `null` = no fill derivable (see the cell notes). */
export const complianceBucketWireSchema = z.enum(['full', 'high', 'medium', 'low']);
export type ComplianceBucketWire = z.infer<typeof complianceBucketWireSchema>;

export const sessionValidityWireSchema = z.enum(['valid', 'invalid', 'not-derivable']);

// ---- per-day heatmap cells --------------------------------------------------

/**
 * One calendar-slot cell of the compliance heatmap (PR8 renders exactly this).
 * The backbone is the A4/B2 calendar's own derivation — `missed` is the
 * calendar's law verbatim, never recomputed here.
 *
 * The states a cell can honestly be in:
 *   - attended (signed session bound): fill from `bucket`; `bucket` null with
 *     `attended` true means the record was too coarse to derive a fill.
 *   - ENDED but not signed off (`sessionId` set, `signedOff` false): no fill —
 *     unsigned data feeds nothing, not even a color (D4). This is the third
 *     state, rendered as "ended · awaiting sign-off".
 *   - missed: the calendar's red state.
 *   - neither (future days, today before its session, or a session that is
 *     still LIVE / pending / dropped on this slot): empty.
 *
 * F23 (RT#12): that third bullet used to read "bound but not signed off", and
 * `sessionId` was populated from the bound session at any status — so a LIVE
 * session was announced as ended and awaiting a signature, on a clinical
 * screen, while Overview correctly called it live. A cell claims the third
 * state only when the session has actually ENDED; anything still in flight is
 * simply empty, because nothing has happened on that slot yet that a physio
 * needs to act on.
 */
export const heatmapCellSchema = z.object({
  slotId: z.uuid(),
  slotDay: z.number().int().min(1),
  week: z.number().int().min(1),
  dayOfWeek: z.number().int().min(1),
  calendarDate: z.iso.date(),
  /** The assessment-day ring (already on the calendar; restated for one-fetch rendering). */
  isAssessment: z.boolean(),
  /** A signed-off session is bound to this slot — the adherence sense of attended. */
  attended: z.boolean(),
  /** The calendar's derived missed verdict (B2), inherited verbatim. */
  missed: z.boolean(),
  /**
   * The session this cell speaks for: the signed one if there is one,
   * otherwise the latest bound session **only once it has ENDED**. Null while
   * a session is pending, live or dropped — see F23 in the block above. The
   * name is deliberately not `boundSessionId`: readers derive the third state
   * from this field, so it must never carry a session that has not finished.
   */
  sessionId: z.uuid().nullable(),
  signedOff: z.boolean(),
  /** Rep-level compliance of the signed session, when derivable. */
  repCompliancePercent: z.number().nullable(),
  bucket: complianceBucketWireSchema.nullable(),
  /** Validity of the signed session (≥ 1 supervised valid set), when signed. */
  validity: sessionValidityWireSchema.nullable(),
  /** Flags RAISED on this calendar day (IST) — the amber dot, with its codes. */
  flagCodes: z.array(flagCodeWireSchema),
});
export type HeatmapCell = z.infer<typeof heatmapCellSchema>;

// ---- the compliance rollup --------------------------------------------------

/** Per-exercise derived totals across the episode (ExerciseRollup). */
export const exerciseRollupSchema = z.object({
  exerciseCode: z.string(),
  /** Planned instances = signed sessions whose plan of record contained it. */
  plannedInstances: z.number().int().min(0),
  completed: z.number().int().min(0),
  partial: z.number().int().min(0),
  skipped: z.number().int().min(0),
  /** Planned instances no outcome row answered (pre-grammar / coarse records). */
  unanswered: z.number().int().min(0),
  /** Σ confirmed setsCompleted / repsLogged numbers, raw (uncapped totals). */
  confirmedSets: z.number().int().min(0),
  confirmedReps: z.number().int().min(0),
});
export type ExerciseRollupEntry = z.infer<typeof exerciseRollupSchema>;

export const complianceRollupSchema = z.object({
  /** All 36 slots of the pinned template's calendar. */
  scheduledTotal: z.number().int().min(0),
  /** Slots whose day has elapsed (IST) — the adherence denominator. */
  scheduledElapsed: z.number().int().min(0),
  /** Slots carrying a signed-off session — the adherence numerator (the flag engine's own counts). */
  attended: z.number().int().min(0),
  /** The calendar's missed verdicts, counted (B2 law: no bound session, day past). */
  missedSlots: z.number().int().min(0),
  /**
   * attended ÷ scheduled-elapsed × 100. Null when nothing has elapsed — an
   * undefined ratio, never 0% (`adherenceTrips`' own elapsed-0 rule).
   */
  adherencePercent: z.number().nullable(),
  /** ADHER_70's floor, restated from protocol data (PROPOSED — OQ4). */
  adherenceFloorPercent: z.number(),
  /** The flag rule's own verdict on today's numbers (display; the EVENT lands at evaluation moments). */
  adherenceBelowFloor: z.boolean(),
  /** Always true while OQ4 is open — no surface renders these numbers unmarked. */
  thresholdsProposed: z.literal(true),
  /** Signed sessions of the episode, slot-bound AND ad-hoc alike. */
  sessions: z.object({
    signedTotal: z.number().int().min(0),
    valid: z.number().int().min(0),
    invalid: z.number().int().min(0),
    notDerivable: z.number().int().min(0),
  }),
  /** Instance mix over planned exercises of signed sessions. */
  exerciseInstances: z.object({
    planned: z.number().int().min(0),
    completed: z.number().int().min(0),
    partial: z.number().int().min(0),
    skipped: z.number().int().min(0),
    unanswered: z.number().int().min(0),
  }),
  /** Rep-level compliance across signed sessions, from confirmed values. */
  repLevel: z.object({
    plannedReps: z.number().int().min(0),
    /** Reps counted toward the plan (per-exercise capped). Null = nothing derivable. */
    countedReps: z.number().int().min(0).nullable(),
    /** Planned reps of the derivable exercises — the denominator actually used. */
    derivablePlannedReps: z.number().int().min(0),
    repCompliancePercent: z.number().nullable(),
    /** Planned exercise instances whose reps could not be derived. */
    exercisesUnknown: z.number().int().min(0),
  }),
  perExercise: z.array(exerciseRollupSchema),
});
export type ComplianceRollup = z.infer<typeof complianceRollupSchema>;

// ---- chart series -----------------------------------------------------------

/**
 * One observation-daily point (the §0.1 daily charts: flexion + extension
 * deficit; pain NRS + swelling). Physio-entered panel values of SIGNED
 * sessions only — a skipped field, or a session sealed before the panel
 * existed, is simply a gap (`null`), never an interpolated point. The
 * assessment-stream series deliberately is NOT here: the assessments route
 * already serves those records, and restating them would fork the source.
 */
export const observationDailyPointSchema = z.object({
  /** The panel's own clinical date (IST) — when the observation was made. */
  calendarDate: z.iso.date(),
  /** Program day when the session was slot-bound; null for ad-hoc sessions. */
  slotDay: z.number().int().min(1).nullable(),
  sessionId: z.uuid(),
  painNrs: z.number().nullable(),
  swellingGirthCm: z.number().nullable(),
  flexionDeg: z.number().nullable(),
  extensionDeficitDeg: z.number().nullable(),
});
export type ObservationDailyPoint = z.infer<typeof observationDailyPointSchema>;

// ---- the response -----------------------------------------------------------

/** GET /api/episodes/:episodeId/rollup */
export const episodeRollupResponseSchema = z.object({
  ok: z.literal(true),
  episodeId: z.uuid(),
  protocolVersion: z.string().min(1),
  /** The IST "today" every derived verdict in this response was computed against. */
  derivedAgainstDate: z.iso.date(),
  compliance: complianceRollupSchema,
  days: z.array(heatmapCellSchema),
  series: z.object({
    observationDaily: z.array(observationDailyPointSchema),
  }),
});
export type EpisodeRollupResponse = z.infer<typeof episodeRollupResponseSchema>;
